import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Users, ChartBar, Megaphone, Trophy, Plus, Trash } from '@phosphor-icons/react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [newChallenge, setNewChallenge] = useState({ title: '', description: '', target_minutes: '', start_date: '', end_date: '' });
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '' });
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const config = { withCredentials: true, headers };
      const [statsRes, usersRes, challengesRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config),
        axios.get(`${API}/challenges`, config),
        axios.get(`${API}/announcements`, config)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setChallenges(challengesRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChallenge = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/challenges`, { ...newChallenge, target_minutes: parseInt(newChallenge.target_minutes) }, { withCredentials: true, headers });
      toast.success('Challenge created!');
      setNewChallenge({ title: '', description: '', target_minutes: '', start_date: '', end_date: '' });
      setChallengeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create challenge');
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/admin/challenges/${challengeId}`, { withCredentials: true, headers });
      toast.success('Challenge deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete challenge');
    }
  };

  const handleCreateAnnouncement = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/announcements`, newAnnouncement, { withCredentials: true, headers });
      toast.success('Announcement created!');
      setNewAnnouncement({ title: '', message: '' });
      setAnnouncementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/admin/announcements/${announcementId}`, { withCredentials: true, headers });
      toast.success('Announcement deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  if (loading) {
    return (<div className="min-h-screen bg-background flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>);
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBar },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'challenges', label: 'Challenges', icon: Trophy },
    { id: 'announcements', label: 'Announcements', icon: Megaphone },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn"><ArrowLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Dashboard</h1><p className="text-xs text-muted-foreground">Manage your app</p></div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid={`tab-${tab.id}`}>
                <Icon weight={activeTab === tab.id ? 'fill' : 'duotone'} className="w-5 h-5" />{tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="premium-card p-5 text-center"><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.total_users || 0}</p><p className="text-xs text-muted-foreground mt-1">Total Users</p></div>
            <div className="premium-card p-5 text-center"><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.total_logs || 0}</p><p className="text-xs text-muted-foreground mt-1">Total Logs</p></div>
            <div className="premium-card p-5 text-center"><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.active_challenges || 0}</p><p className="text-xs text-muted-foreground mt-1">Active Challenges</p></div>
            <div className="premium-card p-5 text-center"><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats?.recent_signups || 0}</p><p className="text-xs text-muted-foreground mt-1">New Users (7d)</p></div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="premium-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-card"><tr><th className="text-left p-4 text-xs text-[#D4AF37] uppercase tracking-wider">Name</th><th className="text-left p-4 text-xs text-[#D4AF37] uppercase tracking-wider">Email</th><th className="text-left p-4 text-xs text-[#D4AF37] uppercase tracking-wider">Role</th><th className="text-left p-4 text-xs text-[#D4AF37] uppercase tracking-wider">Joined</th></tr></thead>
                <tbody>{users.map((u) => (<tr key={u.user_id} className="border-t border-border"><td className="p-4 text-foreground">{u.name}</td><td className="p-4 text-muted-foreground text-sm">{u.email}</td><td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${u.role === 'admin' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-card text-muted-foreground'}`}>{u.role}</span></td><td className="p-4 text-muted-foreground text-sm">{new Date(u.created_at).toLocaleDateString()}</td></tr>))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* Challenges Tab */}
        {activeTab === 'challenges' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={challengeDialogOpen} onOpenChange={setChallengeDialogOpen}>
                <DialogTrigger asChild><Button className="bg-[#D4AF37] text-black rounded-full" data-testid="create-challenge-btn"><Plus weight="bold" className="w-5 h-5 mr-2" />New Challenge</Button></DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader><DialogTitle className="text-foreground">Create Challenge</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div><Label className="text-muted-foreground">Title</Label><Input value={newChallenge.title} onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })} className="mt-2 bg-background" /></div>
                    <div><Label className="text-muted-foreground">Description</Label><Textarea value={newChallenge.description} onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })} className="mt-2 bg-background" /></div>
                    <div><Label className="text-muted-foreground">Target Minutes</Label><Input type="number" value={newChallenge.target_minutes} onChange={(e) => setNewChallenge({ ...newChallenge, target_minutes: e.target.value })} className="mt-2 bg-background" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-muted-foreground">Start Date</Label><Input type="date" value={newChallenge.start_date} onChange={(e) => setNewChallenge({ ...newChallenge, start_date: e.target.value })} className="mt-2 bg-background" /></div>
                      <div><Label className="text-muted-foreground">End Date</Label><Input type="date" value={newChallenge.end_date} onChange={(e) => setNewChallenge({ ...newChallenge, end_date: e.target.value })} className="mt-2 bg-background" /></div>
                    </div>
                    <Button onClick={handleCreateChallenge} className="w-full bg-[#D4AF37] text-black rounded-full">Create Challenge</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {challenges.length === 0 ? (<div className="premium-card p-8 text-center"><Trophy weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No challenges yet</p></div>) : (
              challenges.map((challenge) => (
                <div key={challenge.challenge_id} className="premium-card p-5">
                  <div className="flex items-start justify-between">
                    <div><h3 className="text-foreground font-semibold">{challenge.title}</h3><p className="text-sm text-muted-foreground mt-1">{challenge.description}</p><p className="text-xs text-[#D4AF37] mt-2">{challenge.target_minutes} min target • {challenge.participants_count} participants</p></div>
                    <button onClick={() => handleDeleteChallenge(challenge.challenge_id)} className="text-muted-foreground hover:text-red-500 transition-colors"><Trash weight="duotone" className="w-5 h-5" /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
                <DialogTrigger asChild><Button className="bg-[#D4AF37] text-black rounded-full" data-testid="create-announcement-btn"><Plus weight="bold" className="w-5 h-5 mr-2" />New Announcement</Button></DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader><DialogTitle className="text-foreground">Create Announcement</DialogTitle></DialogHeader>
                  <div className="space-y-4 py-4">
                    <div><Label className="text-muted-foreground">Title</Label><Input value={newAnnouncement.title} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })} className="mt-2 bg-background" /></div>
                    <div><Label className="text-muted-foreground">Message</Label><Textarea value={newAnnouncement.message} onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })} className="mt-2 bg-background" /></div>
                    <Button onClick={handleCreateAnnouncement} className="w-full bg-[#D4AF37] text-black rounded-full">Create Announcement</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {announcements.length === 0 ? (<div className="premium-card p-8 text-center"><Megaphone weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">No announcements yet</p></div>) : (
              announcements.map((announcement) => (
                <div key={announcement.announcement_id} className="premium-card p-5">
                  <div className="flex items-start justify-between">
                    <div><h3 className="text-foreground font-semibold">{announcement.title}</h3><p className="text-sm text-muted-foreground mt-1">{announcement.message}</p></div>
                    <button onClick={() => handleDeleteAnnouncement(announcement.announcement_id)} className="text-muted-foreground hover:text-red-500 transition-colors"><Trash weight="duotone" className="w-5 h-5" /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
